import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useSelector } from "react-redux";
import { alpha, Chip, Skeleton, Tooltip } from "@mui/material";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import { fetchSchoolCurrentTerm } from "../../utils/currentTermUtils";

const SOURCE_HINT = {
    calendar: "Based on term dates in the school calendar.",
    activity: "Based on the latest class results workflow for this school.",
    fees: "Based on active fee structures for this term.",
    "period-api": "Based on the school's official academic year and term records.",
    "open-status": "Based on the term currently marked Open.",
    "calendar-dates": "Based on term open/close dates.",
    explicit: "Set explicitly by the school administrator.",
    default: "Default term until academic years and terms are configured.",
};

/**
 * Shows the school's current term in the office shell header or dashboard hero.
 * variant: "header" | "hero"
 */
export default function CurrentTermIndicator({ variant = "header" }) {
    const { currentUser } = useSelector((state) => state.user);
    const { feeStructures } = useSelector((state) => state.fee);
    const [info, setInfo] = useState(null);
    const [loading, setLoading] = useState(true);

    const schoolId = currentUser?._id;
    const schoolType = currentUser?.schoolType || "Secondary";

    useEffect(() => {
        if (!schoolId) {
            setInfo(null);
            setLoading(false);
            return;
        }

        let cancelled = false;
        setLoading(true);

        fetchSchoolCurrentTerm({
            schoolId,
            schoolType,
            feeStructures,
            includeFees: true,
        })
            .then((result) => {
                if (!cancelled) setInfo(result);
            })
            .catch(() => {
                if (!cancelled) setInfo(null);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [schoolId, schoolType, feeStructures]);

    if (loading) {
        return (
            <Skeleton
                variant="rounded"
                width={variant === "hero" ? 180 : 120}
                height={variant === "hero" ? 32 : 28}
                sx={{
                    mr: variant === "header" ? 1 : 0,
                    bgcolor: variant === "hero" ? alpha("#fff", 0.2) : undefined,
                }}
            />
        );
    }

    if (!info?.label) return null;

    const tooltip = SOURCE_HINT[info.source] || SOURCE_HINT.default;
    const chipLabel =
        variant === "hero" ? `Current term: ${info.label}` : info.label;

    const chip = (
        <Chip
            icon={<EventAvailableOutlinedIcon />}
            label={chipLabel}
            size={variant === "hero" ? "medium" : "small"}
            component={RouterLink}
            to="/Admin/academic-calendar"
            clickable
            sx={
                variant === "hero"
                    ? {
                          mt: 1,
                          bgcolor: alpha("#fff", 0.16),
                          color: "common.white",
                          fontWeight: 700,
                          border: `1px solid ${alpha("#fff", 0.28)}`,
                          "& .MuiChip-icon": { color: "common.white" },
                          "&:hover": { bgcolor: alpha("#fff", 0.24) },
                      }
                    : {
                          mr: 1,
                          color: "inherit",
                          borderColor: alpha("#fff", 0.45),
                          fontWeight: 600,
                          "& .MuiChip-icon": { color: "inherit" },
                      }
            }
            variant={variant === "hero" ? "filled" : "outlined"}
        />
    );

    return <Tooltip title={tooltip}>{chip}</Tooltip>;
}
